import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, MailX, MailCheck, Mail, Download, Printer, AlertTriangle, ShieldAlert, Search, ArrowDownUp } from "lucide-react";
import {
  adminListKycEmailDeliveries,
  adminRetryKycEmail,
  adminExportKycEmailDeliveriesCsv,
  adminListCircuitAlerts,
  adminAckCircuitAlert,
} from "@/lib/helper-kyc.functions";

export const Route = createFileRoute("/_authenticated/admin/kyc/emails")({
  head: () => ({
    meta: [
      { title: "KYC email delivery — Peopels admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: EmailDeliveryPage,
});

type Row = {
  id: string;
  helper_id: string;
  attempt_number: number;
  decision: string;
  template: string;
  recipient_email: string;
  status: "pending" | "sent" | "failed" | string;
  attempt_count: number;
  max_attempts: number;
  last_error: string | null;
  last_attempt_at: string | null;
  next_retry_at: string | null;
  sent_at: string | null;
  created_at: string;
  metadata: Record<string, unknown> | null;
};

type Alert = {
  id: string;
  detected_at: string;
  title: string;
  detail: string | null;
  severity: string;
  window_end: string | null;
  payload: { reason?: string; recipient?: string; decision?: string; helper_id?: string; next_retry_window?: string; attempt_count?: number; max_attempts?: number } | null;
};

const PAGE_SIZE = 50;
type StatusFilter = "all" | "pending" | "sent" | "failed";
type SortKey = "created_at" | "last_attempt_at" | "attempt_count" | "next_retry_at";

function fmt(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function statusBadge(status: string) {
  if (status === "sent") return "bg-emerald-50 text-emerald-700";
  if (status === "failed") return "bg-rose-50 text-rose-700";
  return "bg-amber-50 text-amber-700";
}

function EmailDeliveryPage() {
  const list = useServerFn(adminListKycEmailDeliveries);
  const retry = useServerFn(adminRetryKycEmail);
  const exportCsv = useServerFn(adminExportKycEmailDeliveriesCsv);
  const listAlerts = useServerFn(adminListCircuitAlerts);
  const ackAlert = useServerFn(adminAckCircuitAlert);

  const [status, setStatus] = useState<StatusFilter>("failed");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortKey>("created_at");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [circuitOnly, setCircuitOnly] = useState(false);
  const [page, setPage] = useState(0);

  const filters = { status, q: q.trim() || undefined, sort, order, circuitOnly };

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin-kyc-email-deliveries", status, q, sort, order, circuitOnly, page],
    queryFn: () => list({ data: { ...filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE } }),
    placeholderData: keepPreviousData,
  });

  const alertsQ = useQuery({
    queryKey: ["admin-kyc-circuit-alerts"],
    queryFn: () => listAlerts({ data: { onlyOpen: true, limit: 20 } }),
    refetchInterval: 60_000,
  });
  const alerts = (alertsQ.data?.items ?? []) as Alert[];

  const retryMut = useMutation({
    mutationFn: (vars: { id: string; force?: boolean }) => retry({ data: { deliveryId: vars.id, force: vars.force } }),
    onSuccess: (r) => {
      if ((r as { ok?: boolean })?.ok) toast.success("Email re-sent");
      else toast.error("Retry attempted but still failed — check the row");
      refetch();
      alertsQ.refetch();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Retry failed"),
  });

  const ackMut = useMutation({
    mutationFn: (id: string) => ackAlert({ data: { id } }),
    onSuccess: () => { toast.success("Alert acknowledged"); alertsQ.refetch(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Could not acknowledge"),
  });

  const handleExportCsv = async () => {
    try {
      const r = await exportCsv({ data: { ...filters } });
      const res = r as { csv: string; filename: string; count: number };
      const blob = new Blob([res.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = res.filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${res.count} row${res.count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    }
  };

  const items = (data?.items ?? []) as Row[];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toggleSort = (key: SortKey) => {
    if (sort === key) setOrder(order === "asc" ? "desc" : "asc");
    else { setSort(key); setOrder("desc"); }
    setPage(0);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
          <div>
            <Link to="/admin/kyc" className="text-sm text-muted-foreground hover:text-foreground">← Back to KYC queue</Link>
            <h1 className="mt-1 text-xl font-semibold">KYC email delivery</h1>
            <p className="text-sm text-muted-foreground">Track delivery attempts and retry failed sends for KYC notifications.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/kyc/email-stats" className="text-sm font-medium text-sky-700 hover:underline">View deliverability dashboard →</Link>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
              Refresh
            </Button>
          </div>
        </div>

        {/* Circuit-breaker alerts */}
        {alerts.length > 0 ? (
          <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 p-4 print:hidden">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-rose-900">
              <ShieldAlert className="h-4 w-4" />
              {alerts.length} circuit breaker{alerts.length === 1 ? "" : "s"} open
            </div>
            <ul className="space-y-2">
              {alerts.slice(0, 6).map((a) => (
                <li key={a.id} className="flex flex-wrap items-start justify-between gap-2 rounded-lg bg-white/70 p-3 text-xs text-rose-900">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{a.title}</p>
                    <p className="mt-0.5 text-rose-800/90">
                      {a.detail}
                    </p>
                    <p className="mt-1 text-[11px] text-rose-700">
                      Reason: <span className="font-medium">{a.payload?.reason ?? "unknown"}</span>
                      {" · "}Attempts: {a.payload?.attempt_count}/{a.payload?.max_attempts}
                      {" · "}Next retry window: {fmt(a.payload?.next_retry_window ?? a.window_end)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {/* deep-link placeholder */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { if (a.payload?.recipient) setQ(a.payload.recipient); setCircuitOnly(true); setStatus("all"); setPage(0); }}
                    >
                      <Search className="mr-1 h-3 w-3" /> Find row
                    </Button>
                    <Button size="sm" variant="ghost" disabled={ackMut.isPending} onClick={() => ackMut.mutate(a.id)}>
                      Acknowledge
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
          {(["failed", "pending", "sent", "all"] as const).map((s) => (
            <button
              key={s}
              onClick={() => { setPage(0); setStatus(s); }}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${status === s ? "border-slate-900 bg-slate-900 text-white" : "border-input bg-background hover:bg-accent"}`}
            >
              {s === "sent" ? <MailCheck className="h-3.5 w-3.5" /> : s === "failed" ? <MailX className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
              {s}
            </button>
          ))}
          <label className="ml-2 flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={circuitOnly} onChange={(e) => { setCircuitOnly(e.target.checked); setPage(0); }} />
            Circuit open only
          </label>
          <div className="relative ml-auto">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(0); }}
              placeholder="Search email, error, decision…"
              className="h-9 w-72 rounded-md border border-input bg-background pl-7 pr-2 text-sm"
            />
          </div>
          <Button variant="outline" size="sm" onClick={handleExportCsv} title="Export current view as CSV">
            <Download className="mr-1.5 h-4 w-4" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} title="Print / save as PDF">
            <Printer className="mr-1.5 h-4 w-4" /> PDF
          </Button>
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border bg-white p-10 text-center text-sm text-muted-foreground">
            No deliveries match the current filter.
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <SortHeader label="Created" k="created_at" sort={sort} order={order} onClick={toggleSort} />
                  <th className="p-3 text-left">Helper</th>
                  <th className="p-3 text-left">Decision</th>
                  <th className="p-3 text-left">Recipient</th>
                  <th className="p-3 text-left">Status</th>
                  <SortHeader label="Attempts" k="attempt_count" sort={sort} order={order} onClick={toggleSort} />
                  <SortHeader label="Last attempt" k="last_attempt_at" sort={sort} order={order} onClick={toggleSort} />
                  <th className="p-3 text-left">Last error</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t align-top">
                    <td className="p-3 text-xs text-muted-foreground">{fmt(r.created_at)}</td>
                    <td className="p-3">
                      <Link to="/admin/kyc/$helperId" params={{ helperId: r.helper_id }} className="font-mono text-xs text-sky-700 hover:underline">
                        {r.helper_id.slice(0, 8)}…
                      </Link>
                      <div className="text-xs text-muted-foreground">attempt #{r.attempt_number}</div>
                    </td>
                    <td className="p-3 text-sm">{r.decision.replaceAll("_", " ")}</td>
                    <td className="p-3 text-xs">{r.recipient_email}</td>
                    <td className="p-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge(r.status)}`}>{r.status}</span>
                      {(r.metadata as { circuit_open?: boolean } | null)?.circuit_open ? (
                        <div className="mt-1 inline-flex items-center gap-1 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-800">
                          <AlertTriangle className="h-2.5 w-2.5" /> circuit open
                        </div>
                      ) : null}
                    </td>
                    <td className="p-3 text-xs">
                      {r.attempt_count} / {r.max_attempts}
                      {r.next_retry_at && r.status === "pending" ? (
                        <div className="text-[10px] text-muted-foreground">next retry {fmt(r.next_retry_at)}</div>
                      ) : null}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{fmt(r.last_attempt_at)}</td>
                    <td className="p-3 max-w-xs">
                      {r.last_error ? <p className="truncate text-xs text-rose-700" title={r.last_error}>{r.last_error}</p> : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="p-3 text-right print:hidden">
                      <div className="flex justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={retryMut.isPending || r.status === "sent" || r.attempt_count >= r.max_attempts}
                          onClick={() => retryMut.mutate({ id: r.id })}
                          title={r.next_retry_at && new Date(r.next_retry_at) > new Date() ? `Available after ${fmt(r.next_retry_at)}` : "Retry now"}
                        >
                          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                          Retry
                        </Button>
                        {(r.status === "failed" || r.attempt_count >= r.max_attempts) ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={retryMut.isPending || r.status === "sent"}
                            onClick={() => retryMut.mutate({ id: r.id, force: true })}
                            title="Override the circuit breaker and try one more send"
                          >
                            Force
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 ? (
          <div className="mt-6 flex items-center justify-between gap-3">
            <Button variant="outline" size="sm" disabled={page === 0 || isFetching} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
            <span className="text-xs text-muted-foreground">Page {page + 1} of {totalPages} · {total} rows · sorted by {sort} {order}</span>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages || isFetching} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}

function SortHeader({ label, k, sort, order, onClick }: { label: string; k: SortKey; sort: SortKey; order: "asc" | "desc"; onClick: (k: SortKey) => void }) {
  const active = sort === k;
  return (
    <th className="p-3 text-left">
      <button
        type="button"
        onClick={() => onClick(k)}
        className={`inline-flex items-center gap-1 ${active ? "text-slate-900" : ""}`}
      >
        {label}
        <ArrowDownUp className={`h-3 w-3 ${active ? "opacity-100" : "opacity-30"}`} />
        {active ? <span className="text-[10px]">{order}</span> : null}
      </button>
    </th>
  );
}
